import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select, Textarea, EmptyState } from "../components/UI";
import {
  Gift, Loader2, Plus, QrCode, Ban, Download, TicketPercent, Coins, X, Copy, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { PromoTabs } from "../components/promo/PromoTabs";

const usd = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fb;
};
const KIND_META = {
  gift_card: { label: "eGift Card", icon: Gift, cls: "border-cyan-400/30 bg-cyan-400/10 text-cyan-300" },
  coupon: { label: "Coupon", icon: TicketPercent, cls: "border-violet-400/30 bg-violet-400/10 text-violet-300" },
  campaign_credit: { label: "Campaign Credit", icon: Coins, cls: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" },
};
const CODE_STATUS = {
  issued: "border-cyan-400/25 bg-cyan-400/5 text-cyan-300",
  claimed: "border-amber-400/25 bg-amber-400/5 text-amber-300",
  redeemed: "border-emerald-400/25 bg-emerald-400/5 text-emerald-300",
  void: "border-rose-400/25 bg-rose-400/5 text-rose-300",
};
const valueLabel = (p) => (p.value_type === "percent" ? `${p.value}% off` : usd(p.value));

const EMPTY_FORM = {
  name: "", kind: "gift_card", value_type: "fixed", value: "", description: "",
  applies_to: ["campaign_launch"], stackable: false, max_per_user: 1, min_purchase: 0,
  new_users_only: false, expires_at: "", code_prefix: "TR", initial_codes: 10,
};

function CreateModal({ onClose, onCreated }) {
  const [f, setF] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleApplies = (a) => set("applies_to", f.applies_to.includes(a)
    ? f.applies_to.filter((x) => x !== a) : [...f.applies_to, a]);

  const submit = async () => {
    if (!f.name.trim() || !f.value) return toast.error("Name and value are required");
    setBusy(true);
    try {
      const payload = {
        ...f, value: parseFloat(f.value), max_per_user: parseInt(f.max_per_user) || 1,
        min_purchase: parseFloat(f.min_purchase) || 0, initial_codes: parseInt(f.initial_codes) || 0,
        expires_at: f.expires_at || null,
      };
      const { data } = await api.post("/promotions", payload);
      toast.success(`Promotion created — ${data.codes_created} code(s) issued`);
      onCreated();
    } catch (e) {
      toast.error(errMsg(e, "Could not create the promotion"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="promo-create-modal">
      <Card className="w-full max-w-2xl !p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-bold">New promotion</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="promo-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Trade show $250 credit" data-testid="promo-name-input" />
          <Select label="Type" value={f.kind} data-testid="promo-kind-select"
            onChange={(e) => { const k = e.target.value; set("kind", k); if (k !== "coupon") set("value_type", "fixed"); if (k === "campaign_credit") set("applies_to", ["campaign_launch"]); }}>
            <option value="gift_card">eGift Card (spendable balance)</option>
            <option value="coupon">Coupon (discount)</option>
            <option value="campaign_credit">Campaign Credit (ad launches only)</option>
          </Select>
          <Select label="Value type" value={f.value_type} onChange={(e) => set("value_type", e.target.value)} disabled={f.kind !== "coupon"} data-testid="promo-value-type-select">
            <option value="fixed">Fixed amount ($)</option>
            {f.kind === "coupon" && <option value="percent">Percent off (%)</option>}
          </Select>
          <Input label={f.value_type === "percent" ? "Percent off" : "Value ($)"} type="number" min="0" value={f.value} onChange={(e) => set("value", e.target.value)} data-testid="promo-value-input" />
          <Input label="Code prefix" value={f.code_prefix} onChange={(e) => set("code_prefix", e.target.value.toUpperCase())} hint="Codes look like TR-8FKD-2MQP" data-testid="promo-prefix-input" />
          <Input label="Initial batch (codes to issue now)" type="number" min="0" max="500" value={f.initial_codes} onChange={(e) => set("initial_codes", e.target.value)} data-testid="promo-batch-input" />
          <Input label="Expiration date" type="date" value={f.expires_at} onChange={(e) => set("expires_at", e.target.value)} data-testid="promo-expires-input" />
          <Input label="Max redemptions per user" type="number" min="1" value={f.max_per_user} onChange={(e) => set("max_per_user", e.target.value)} data-testid="promo-max-per-user-input" />
          <Input label="Minimum purchase ($, 0 = none)" type="number" min="0" value={f.min_purchase} onChange={(e) => set("min_purchase", e.target.value)} data-testid="promo-min-purchase-input" />
        </div>
        <Textarea label="Description (shown to the recipient)" value={f.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="promo-description-input" />
        <div className="mt-4 space-y-2">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Applies to</div>
          <div className="flex gap-2 flex-wrap">
            {[["campaign_launch", "Campaign launches"], ["subscription", "Subscriptions"], ["any", "Anything (except fundraising)"]].map(([k, label]) => (
              <button key={k} onClick={() => f.kind !== "campaign_credit" && toggleApplies(k)} data-testid={`promo-applies-${k}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${f.applies_to.includes(k) ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-slate-400"} ${f.kind === "campaign_credit" ? "opacity-60 cursor-not-allowed" : ""}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-amber-300/80">Fundraising donations are always excluded from promotions.</div>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={f.stackable} onChange={(e) => set("stackable", e.target.checked)} data-testid="promo-stackable-checkbox" /> Stackable with other promos
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={f.new_users_only} onChange={(e) => set("new_users_only", e.target.checked)} data-testid="promo-new-users-checkbox" /> New users only
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} disabled={busy} data-testid="promo-create-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create promotion
          </Btn>
        </div>
      </Card>
    </div>
  );
}

function CodesDrawer({ promo, onClose, onChanged }) {
  const [codes, setCodes] = useState(null);
  const [batch, setBatch] = useState(25);
  const [busy, setBusy] = useState(false);
  const [qrView, setQrView] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/promotions/${promo.id}/codes?limit=200`);
      setCodes(data.codes || []);
    } catch (e) { toast.error(errMsg(e, "Could not load codes")); }
  }, [promo.id]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/promotions/${promo.id}/codes`, { count: parseInt(batch) || 1 });
      toast.success(`${data.created} new code(s) issued`);
      await load(); onChanged();
    } catch (e) { toast.error(errMsg(e, "Could not issue codes")); } finally { setBusy(false); }
  };

  const voidCode = async (c) => {
    if (!window.confirm(`Void code ${c.code}? It can never be claimed or redeemed.`)) return;
    try {
      await api.post(`/promotions/${promo.id}/codes/${c.id}/void`);
      toast.success("Code voided");
      await load(); onChanged();
    } catch (e) { toast.error(errMsg(e, "Void failed")); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Copy failed"); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/promotions/${promo.id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `promo-codes-${promo.code_prefix || "TR"}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e, "Export failed")); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="promo-codes-drawer">
      <Card className="w-full max-w-3xl !p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <div className="font-display text-lg font-bold">{promo.name} — codes</div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="codes-drawer-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-xs text-slate-400 mb-4">{valueLabel(promo)} · {KIND_META[promo.kind]?.label}{promo.expires_at ? ` · expires ${promo.expires_at.slice(0, 10)}` : ""}</div>
        <div className="flex items-end gap-2 mb-4 flex-wrap">
          <Input label="Issue more codes" type="number" min="1" max="500" value={batch} onChange={(e) => setBatch(e.target.value)} className="w-36" data-testid="codes-batch-input" />
          <Btn onClick={generate} disabled={busy} data-testid="codes-generate-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Generate
          </Btn>
          <Btn variant="secondary" onClick={exportCsv} data-testid="codes-export-btn"><Download className="w-4 h-4" /> Export CSV</Btn>
          <Btn variant="secondary" onClick={load}><RefreshCw className="w-4 h-4" /></Btn>
        </div>
        {!codes ? (
          <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading codes…</div>
        ) : codes.length === 0 ? (
          <div className="text-sm text-slate-500">No codes issued yet — generate a batch above.</div>
        ) : (
          <div className="space-y-1.5" data-testid="codes-list">
            {codes.map((c) => (
              <div key={c.id} className="flex items-center gap-3 rounded-lg border border-white/10 px-3 py-2 text-xs" data-testid={`code-row-${c.code}`}>
                <button onClick={() => setQrView(qrView === c.id ? null : c.id)} className="text-cyan-300 hover:text-cyan-100" title="Show QR" data-testid={`code-qr-btn-${c.code}`}>
                  <QrCode className="w-4 h-4" />
                </button>
                <span className="font-mono text-slate-200">{c.code}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${CODE_STATUS[c.status] || "border-white/10 text-slate-400"}`}>{c.status}</span>
                {c.remaining_value != null && <span className="text-slate-400">{usd(c.remaining_value)} left</span>}
                {c.claimed_by && <span className="text-slate-500 truncate">→ {c.claimed_by}</span>}
                <span className="ml-auto flex items-center gap-2">
                  <button onClick={() => copy(c.claim_url)} className="text-slate-400 hover:text-cyan-300" title="Copy claim link"><Copy className="w-3.5 h-3.5" /></button>
                  {["issued", "claimed"].includes(c.status) && (
                    <button onClick={() => voidCode(c)} className="text-slate-400 hover:text-rose-300" title="Void" data-testid={`code-void-btn-${c.code}`}><Ban className="w-3.5 h-3.5" /></button>
                  )}
                </span>
                {qrView === c.id && c.qr_data_uri && (
                  <img src={c.qr_data_uri} alt={`QR ${c.code}`} className="w-24 h-24 rounded bg-paper p-1" data-testid={`code-qr-img-${c.code}`} />
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export default function AdminPromotions() {
  const [data, setData] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [drawer, setDrawer] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/promotions");
      setData(data.promotions || []);
    } catch (e) { toast.error(errMsg(e, "Could not load promotions")); setData([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (p) => {
    try {
      await api.put(`/promotions/${p.id}`, { active: !p.active });
      toast.success(p.active ? "Promotion deactivated" : "Promotion reactivated");
      load();
    } catch (e) { toast.error(errMsg(e, "Update failed")); }
  };

  const totals = (data || []).reduce((a, p) => ({
    active: a.active + (p.active ? 1 : 0),
    codes: a.codes + (p.stats?.total_codes || 0),
    claimed: a.claimed + (p.stats?.claimed || 0) + (p.stats?.redeemed || 0),
    outstanding: a.outstanding + (p.stats?.outstanding_value || 0),
  }), { active: 0, codes: 0, claimed: 0, outstanding: 0 });

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-promotions-page">
      <PageHeader eyebrow="Admin" title="Promotions & Credits"
        subtitle="Issue eGift cards, coupons and campaign credits with unique codes + QR. Fundraising donations are always excluded."
        action={<Btn onClick={() => setShowCreate(true)} data-testid="new-promotion-btn"><Plus className="w-4 h-4" /> New promotion</Btn>} />
      <PromoTabs />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="promo-totals">
        <Card className="!p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">Active promotions</div><div className="font-display text-2xl font-bold">{totals.active}</div></Card>
        <Card className="!p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">Codes issued</div><div className="font-display text-2xl font-bold">{totals.codes}</div></Card>
        <Card className="!p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">Claimed / redeemed</div><div className="font-display text-2xl font-bold">{totals.claimed}</div></Card>
        <Card className="!p-4"><div className="text-[10px] uppercase tracking-wider text-slate-500">Outstanding value</div><div className="font-display text-2xl font-bold text-cyan-300">{usd(totals.outstanding)}</div></Card>
      </div>

      {!data ? (
        <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading promotions…</div>
      ) : data.length === 0 ? (
        <EmptyState icon={Gift} title="No promotions yet"
          description="Create your first eGift card, coupon or campaign credit — perfect for trade shows and client incentives."
          action={<Btn onClick={() => setShowCreate(true)} data-testid="empty-new-promotion-btn"><Plus className="w-4 h-4" /> New promotion</Btn>} />
      ) : (
        <div className="space-y-3">
          {data.map((p) => {
            const meta = KIND_META[p.kind] || KIND_META.gift_card;
            const Icon = meta.icon;
            return (
              <Card key={p.id} className="!p-4" data-testid={`promotion-card-${p.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Icon className="w-4 h-4 text-cyan-300" />
                      <span className="font-display font-bold text-sm">{p.name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${meta.cls}`}>{meta.label}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-slate-300">{valueLabel(p)}</span>
                      {!p.active && <span className="text-[10px] px-2 py-0.5 rounded-full border border-rose-400/25 bg-rose-400/5 text-rose-300">Inactive</span>}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1.5 flex gap-3 flex-wrap">
                      <span>{p.stackable ? "Stackable" : "Not stackable"}</span>
                      <span>Max {p.max_per_user}/user</span>
                      {p.min_purchase > 0 && <span>Min purchase {usd(p.min_purchase)}</span>}
                      {p.new_users_only && <span>New users only</span>}
                      <span>{p.expires_at ? `Expires ${p.expires_at.slice(0, 10)}` : "No expiration"}</span>
                      <span>Applies to: {(p.applies_to || []).join(", ").replace(/_/g, " ")}</span>
                    </div>
                    <div className="text-xs text-slate-300 mt-2 flex gap-4 flex-wrap" data-testid={`promo-stats-${p.id}`}>
                      <span>Codes <b>{p.stats.total_codes}</b></span>
                      <span>Unclaimed <b>{p.stats.issued || 0}</b></span>
                      <span>Claimed <b>{p.stats.claimed || 0}</b></span>
                      <span>Redeemed <b>{p.stats.redeemed || 0}</b></span>
                      <span>Void <b>{p.stats.void || 0}</b></span>
                      {p.kind !== "coupon" && <span className="text-cyan-300">Outstanding <b>{usd(p.stats.outstanding_value)}</b></span>}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    <Btn variant="secondary" onClick={() => setDrawer(p)} data-testid={`view-codes-btn-${p.id}`}><QrCode className="w-4 h-4" /> Codes</Btn>
                    <Btn variant="secondary" onClick={() => toggleActive(p)} data-testid={`toggle-active-btn-${p.id}`}>
                      {p.active ? <><Ban className="w-4 h-4" /> Deactivate</> : <><RefreshCw className="w-4 h-4" /> Reactivate</>}
                    </Btn>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {drawer && <CodesDrawer promo={drawer} onClose={() => setDrawer(null)} onChanged={load} />}
    </div>
  );
}
